import React, { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { api, formatCurrency, formatDate } from '../../utils/api'
import { FILTERABLE_STAGES, dealStageLabel, parseItems, parseItemLine } from '../../utils/dealConstants'
import DealDetailModal from './DealDetailModal'
import DealLink from '../shared/DealLink.jsx'

// Turn the Prism /api/v1/deals/search rows (one per matched item) into the deal
// row shape this grid already renders. The server-side search is the ONLY item
// search that scales: the old client-side `parsedItems` filter had to fetch the
// whole portal via `?all=true` first, which now exceeds the worker's resource
// limit (Cloudflare 1102).
//
// A synthesised `parsed_items` blob rather than a bespoke render path: the grid,
// stats, CSV export and the detail modal all read items back through
// parseItemLine, so feeding them the one string they already understand keeps a
// single rendering path. The blob is built in the exact tag format parseItemLine
// round-trips (SP:£/BP:£/Qty:), so nothing downstream needs to know the row came
// from search. Rows that matched only on the deal name carry a null item_name
// (deal_search schema) and contribute no item line — the deal still shows via its
// dealname.
function itemLineFromSearchRow(row) {
  const segments = [row.item_name]
  if (row.product_category) segments.push(row.product_category)
  if (row.condition) segments.push(row.condition)
  let line = segments.filter(Boolean).join(' - ')
  line += ` - Qty:${row.quantity != null ? row.quantity : 1}`
  if (row.sell_price_cents != null) line += ` - SP:£${row.sell_price_cents / 100}`
  if (row.buy_price_cents != null) line += ` - BP:£${row.buy_price_cents / 100}`
  return line
}

export function mapSearchResultsToDeals(results) {
  const byDeal = new Map()
  for (const row of results || []) {
    const id = row.hubspot_deal_id
    if (!byDeal.has(id)) {
      byDeal.set(id, {
        id,
        dealname: row.deal_name,
        // dealStageLabel reads stageName first; the endpoint already resolved
        // both stage vocabularies into a human label, so carry it as-is.
        stageName: row.deal_stage_label,
        dealstage: row.deal_stage,
        stage: row.deal_stage,
        createdate: row.deal_create_date,
        seller_email: row.contact_email,
        contact_email: row.contact_email,
        amount: 0,
        lines: [],
      })
    }
    const deal = byDeal.get(id)
    if (row.item_name) {
      deal.lines.push(itemLineFromSearchRow(row))
      // The deal's value is the sum of its items' buy prices — qty-aware,
      // because buy_price_cents is per unit; the same rule totalBuyPrice
      // enforces elsewhere.
      if (row.buy_price_cents != null) deal.amount += (row.buy_price_cents / 100) * (row.quantity || 1)
    }
  }
  return Array.from(byDeal.values()).map(({ lines, amount, ...deal }) => ({
    ...deal,
    parsed_items: lines.join('\n'),
    amount: String(amount),
  }))
}

function AllDeals() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [deals, setDeals] = useState([])
  const [loading, setLoading] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const [allDealsCache, setAllDealsCache] = useState([])
  const [expandedDeals, setExpandedDeals] = useState(new Set())

  // Check for dealId, stage, or email in URL to auto-open/filter
  // Using searchParams as dependency so it re-runs when URL params change
  useEffect(() => {
    const dealIdParam = searchParams.get('dealId')
    const stageParam = searchParams.get('stage')
    const emailParam = searchParams.get('email')

    if (dealIdParam) {
      // Load this specific deal and open its modal
      loadAndOpenDeal(dealIdParam)
      // Clean up URL by removing the dealId param
      setSearchParams({}, { replace: true })
    } else if (emailParam) {
      // Pre-fill email and auto-search
      setSearchFilters(prev => ({ ...prev, email: emailParam }))
      setAdvancedExpanded(true) // Show the email field
      // Clean up URL
      setSearchParams({}, { replace: true })
      // Trigger search after a short delay to let state update
      setTimeout(() => {
        searchDealsWithEmail(emailParam)
      }, 100)
    } else if (stageParam) {
      // Pre-filter by stage and auto-search
      setSearchFilters(prev => ({ ...prev, dealStage: stageParam }))
      // Clean up URL
      setSearchParams({}, { replace: true })
      // Trigger search after a short delay to let state update
      setTimeout(() => {
        searchDealsWithStage(stageParam)
      }, 100)
    }
  }, [searchParams])

  // Search with a specific stage (for URL-based filtering).
  // Stage filtering is server-side: fetching all deals and filtering in the
  // browser pages through the entire HubSpot portal and times out.
  async function searchDealsWithStage(stage) {
    setLoading(true)
    setHasSearched(true)
    setSearchError(null)
    setIncomplete(null)
    try {
      const response = await api.get(`/api/deals?stage=${encodeURIComponent(stage)}`)
      setDeals(response.deals || [])
      setIncomplete(response.incomplete || null)
    } catch (error) {
      console.error('Error searching deals:', error)
      setSearchError(error.message || 'Deal search failed')
      setDeals([])
    } finally {
      setLoading(false)
    }
  }

  // Search with a specific email (for URL-based filtering from Tickets)
  async function searchDealsWithEmail(email) {
    setLoading(true)
    setHasSearched(true)
    setSearchError(null)
    setIncomplete(null)
    try {
      // Use the email search endpoint
      const response = await api.get(`/api/deals?email=${encodeURIComponent(email)}`)
      setDeals(response.deals || [])
      setIncomplete(response.incomplete || null)
    } catch (error) {
      console.error('Error searching deals by email:', error)
      setSearchError(error.message || 'Deal search failed')
      setDeals([])
    } finally {
      setLoading(false)
    }
  }

  async function loadAndOpenDeal(dealId) {
    try {
      setLoading(true)
      const response = await api.get(`/api/deals/${dealId}`)
      if (response.deal) {
        setSelectedDeal(response.deal)
      }
    } catch (error) {
      console.error('Error loading deal:', error)
    } finally {
      setLoading(false)
    }
  }

  // Advanced search filters
  const [searchFilters, setSearchFilters] = useState({
    email: '',
    dealId: '',
    parsedItems: '',
    dateFrom: '',
    dateTo: '',
    dealStage: '',
    trackingNumber: '',
    collectionDateFrom: '',
    collectionDateTo: '',
    noCollectionDate: false
  })
  const [advancedExpanded, setAdvancedExpanded] = useState(false)
  const [searchError, setSearchError] = useState(null)
  // Set when the worker answers with rows AND an admission that it could not
  // load all of them. Null means the answer is complete — an absent key and a
  // complete answer must not look the same to the operator.
  const [incomplete, setIncomplete] = useState(null)

  // Deal detail modal
  const [selectedDeal, setSelectedDeal] = useState(null)

  // Search deals.
  //
  // `bypassCache` exists for Retry. The unfiltered branch below reuses
  // allDealsCache, and this function clears `incomplete` at the top — so a
  // cached Retry re-rendered the SAME truncated rows with the warning gone,
  // turning a known-incomplete list into an apparently-complete one.
  //
  // It is a PARAMETER rather than `setAllDealsCache([])` at the call site: state
  // updates are not visible to the invocation that queued them, so clearing the
  // cache and calling this in the same handler would still read the stale value
  // out of the closure and take the cached branch anyway.
  //
  // Guarded with `=== true` because this is also a click handler, and a React
  // event object in the parameter slot must not read as an opts bag.
  async function searchDeals(opts) {
    const bypassCache = opts?.bypassCache === true
    setLoading(true)
    setHasSearched(true)
    setSearchError(null)
    setIncomplete(null)
    try {
      let dealsToFilter
      const hasDateFilter = searchFilters.dateFrom || searchFilters.dateTo

      // A bare item search — `parsedItems` and nothing else — is the one case that
      // used to fall through to `?all=true` (fetch the whole portal, filter items
      // in the browser). That single worker invocation now exceeds Cloudflare's
      // resource limit and 500s with error 1102. Route it to the indexed
      // server-side search instead. Only when it stands alone: combined with any
      // other filter the branches below already narrow the set server-side, and
      // those narrowing fields (email/dealId/stage/date) plus the client-side ones
      // (tracking/collection) are not carried on search rows, so they must keep
      // filtering the fuller result the old path returns.
      const itemQuery = searchFilters.parsedItems.trim()
      const itemSearchOnly = itemQuery
        && !searchFilters.email && !searchFilters.dealId && !searchFilters.dealStage
        && !hasDateFilter && !searchFilters.trackingNumber
        && !searchFilters.collectionDateFrom && !searchFilters.collectionDateTo
        && !searchFilters.noCollectionDate
      if (itemSearchOnly) {
        const response = await api.get(`/api/deals/search?q=${encodeURIComponent(itemQuery)}`)
        setDeals(mapSearchResultsToDeals(response.results))
        // The endpoint caps at 500 matched rows. Say so — but not with the
        // "could not be loaded" copy; nothing failed, the answer is just capped.
        setIncomplete(response.truncated
          ? { count: (response.results || []).length, reason: 'truncated' }
          : null)
        return
      }

      // If deal ID is provided, use server-side direct lookup
      if (searchFilters.dealId) {
        const response = await api.get(`/api/deals?dealId=${encodeURIComponent(searchFilters.dealId)}`)
        dealsToFilter = response.deals || []
        setIncomplete(response.incomplete || null)
      } else if (searchFilters.email) {
        // If email is provided, use server-side email search
        const response = await api.get(`/api/deals?email=${encodeURIComponent(searchFilters.email)}`)
        dealsToFilter = response.deals || []
        setIncomplete(response.incomplete || null)
      } else if (searchFilters.dealStage || hasDateFilter) {
        // Stage and/or date filter - server-side (fetching all deals to
        // filter in the browser pages the whole portal and times out)
        const params = new URLSearchParams()
        if (searchFilters.dealStage) params.append('stage', searchFilters.dealStage)
        else params.append('all', 'true')
        if (searchFilters.dateFrom) params.append('dateFrom', searchFilters.dateFrom)
        if (searchFilters.dateTo) params.append('dateTo', searchFilters.dateTo)
        const response = await api.get(`/api/deals?${params.toString()}`)
        dealsToFilter = response.deals || []
        setIncomplete(response.incomplete || null)
      } else {
        // No specific filter - use cached all deals
        if (bypassCache || allDealsCache.length === 0) {
          const response = await api.get('/api/deals?all=true')
          dealsToFilter = response.deals || []
          setIncomplete(response.incomplete || null)
          setAllDealsCache(dealsToFilter)
        } else {
          dealsToFilter = allDealsCache
        }
      }

      // Apply client-side filters (deal ID is now server-side)
      const filtered = dealsToFilter.filter(deal => {
        // Parsed Items filter
        if (searchFilters.parsedItems) {
          const itemsLower = searchFilters.parsedItems.toLowerCase()
          if (!(deal.parsed_items || '').toLowerCase().includes(itemsLower)) return false
        }

        // Deal Stage filter
        if (searchFilters.dealStage) {
          // Either vocabulary may be the filter: `expired`/`collected` have no
          // HubSpot id, so the row matches on its canonical `stage` instead.
          // Comparing `dealstage` alone dropped every row the server had just
          // been asked for, and the grid said "No Deals Found" holding 100 of them.
          if (deal.dealstage !== searchFilters.dealStage && deal.stage !== searchFilters.dealStage) return false
        }

        // Tracking Number filter (supports comma-separated list)
        if (searchFilters.trackingNumber) {
          const dealTracking = (deal.tracking_number || '').toLowerCase()
          const searchTerms = searchFilters.trackingNumber.split(',').map(t => t.trim().toLowerCase()).filter(Boolean)
          if (!searchTerms.some(term => dealTracking.includes(term))) return false
        }

        // No Collection Date filter
        if (searchFilters.noCollectionDate) {
          if (deal.collection_date) return false
        }

        // Collection Date From
        if (searchFilters.collectionDateFrom && !searchFilters.noCollectionDate) {
          if (!deal.collection_date) return false
          const collDate = new Date(deal.collection_date)
          const fromDate = new Date(searchFilters.collectionDateFrom)
          if (collDate < fromDate) return false
        }

        // Collection Date To
        if (searchFilters.collectionDateTo && !searchFilters.noCollectionDate) {
          if (!deal.collection_date) return false
          const collDate = new Date(deal.collection_date)
          const toDate = new Date(searchFilters.collectionDateTo)
          toDate.setHours(23, 59, 59, 999)
          if (collDate > toDate) return false
        }

        return true
      })

      setDeals(filtered)
    } catch (error) {
      console.error('Error searching deals:', error)
      setSearchError(error.message || 'Deal search failed')
      setDeals([])
    } finally {
      setLoading(false)
    }
  }

  // Clear filters
  function clearFilters() {
    setSearchFilters({
      email: '',
      dealId: '',
      parsedItems: '',
      dateFrom: '',
      dateTo: '',
      dealStage: '',
      trackingNumber: '',
      collectionDateFrom: '',
      collectionDateTo: '',
      noCollectionDate: false
    })
    setAdvancedExpanded(false)
    setDeals([])
    setHasSearched(false)
    setSearchError(null)
    setIncomplete(null)
  }

  // Refresh cache
  function refreshCache() {
    setAllDealsCache([])
    setDeals([])
    setHasSearched(false)
  }

  // Update search filter
  function updateFilter(field, value) {
    setSearchFilters(prev => ({ ...prev, [field]: value }))
  }

  // Open deal in modal
  function openDealModal(deal) {
    setSelectedDeal(deal)
  }

  // Close modal
  function closeDealModal() {
    setSelectedDeal(null)
  }

  // Refresh deals after modal changes
  function refreshDeals() {
    setAllDealsCache([])
    if (hasSearched) searchDeals()
  }

  // Export current results as CSV
  function exportCSV() {
    const escCSV = (val) => {
      const s = String(val ?? '')
      return s.includes(',') || s.includes('"') || s.includes('\n')
        ? '"' + s.replace(/"/g, '""') + '"'
        : s
    }

    const rows = [['Deal ID', 'Deal Name', 'Email', 'Stage', 'Created', 'Item #', 'Item Name', 'Product Type', 'Condition', 'Qty', 'SP', 'BP', 'Score']]

    for (const deal of deals) {
      const items = parseItems(deal.parsed_items)
      const email = deal.seller_email || deal.contact_email || ''
      const stage = dealStageLabel(deal)
      const created = deal.createdate ? new Date(deal.createdate).toISOString().slice(0, 10) : ''

      if (items.length === 0) {
        rows.push([deal.id, deal.dealname || '', email, stage, created, '', '(no parsed items)', '', '', '', '', '', ''])
      } else {
        items.forEach((line, idx) => {
          const item = parseItemLine(line)
          rows.push([
            deal.id,
            deal.dealname || '',
            email,
            stage,
            created,
            idx + 1,
            item?.name || line,
            item?.details || '',
            item?.condition || '',
            item?.qty || 1,
            item?.sp != null ? item.sp : '',
            item?.bp != null ? item.bp : '',
            item?.score != null ? item.score : ''
          ])
        })
      }
    }

    const csv = rows.map(r => r.map(escCSV).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `deals-export-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  // Calculate stats from current results
  const totalValue = deals.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0)
  const totalItems = deals.reduce((sum, d) => sum + parseItems(d.parsed_items).length, 0)

  // Count by stage
  const stageCounts = deals.reduce((acc, d) => {
    const stage = dealStageLabel(d)
    acc[stage] = (acc[stage] || 0) + 1
    return acc
  }, {})

  // Get top stages
  const topStages = Object.entries(stageCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)

  return (
    <div>
      {/* Stats */}
      {hasSearched && deals.length > 0 && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-value">{deals.length}</div>
            <div className="stat-label">Deals Found</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{totalItems}</div>
            <div className="stat-label">Total Items</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{formatCurrency(totalValue)}</div>
            <div className="stat-label">Total Value</div>
          </div>
          {topStages.map(([stage, count], i) => (
            <div className="stat-card" key={i}>
              <div className="stat-value">{count}</div>
              <div className="stat-label">{stage}</div>
            </div>
          ))}
        </div>
      )}

      {/* Search Filters */}
      <div className="card" style={{ marginTop: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
        {/* Primary filters */}
        <div className="filter-section">
          <div className="filter-group filter-group-wide">
            <label className="filter-label">Email</label>
            <input
              type="text"
              className="form-input"
              placeholder="Search by email..."
              value={searchFilters.email}
              onChange={e => updateFilter('email', e.target.value)}
              onKeyDown={e => e.key === 'Enter' && searchDeals()}
            />
          </div>
          <div className="filter-group">
            <label className="filter-label">Deal ID / Ref</label>
            <input
              type="text"
              className="form-input"
              placeholder="509818609864 or BLK-4A8F7..."
              value={searchFilters.dealId}
              onChange={e => updateFilter('dealId', e.target.value)}
              onKeyDown={e => e.key === 'Enter' && searchDeals()}
            />
          </div>
          <div className="filter-group">
            <label className="filter-label">Stage</label>
            <select
              className="form-input"
              value={searchFilters.dealStage}
              onChange={e => updateFilter('dealStage', e.target.value)}
            >
              {FILTERABLE_STAGES.map(stage => (
                <option key={stage.id} value={stage.id}>{stage.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Advanced Toggle */}
        <button
          type="button"
          className="filter-toggle"
          onClick={() => setAdvancedExpanded(!advancedExpanded)}
        >
          <span className={`filter-toggle-icon ${advancedExpanded ? 'expanded' : ''}`}>▼</span>
          {advancedExpanded ? 'Hide' : 'Show'} Advanced Search
        </button>

        {/* Advanced filters */}
        {advancedExpanded && (
          <div className="advanced-filters">
            <div className="filter-section">
              <div className="filter-group">
                <label className="filter-label">Parsed Items</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Search in items..."
                  value={searchFilters.parsedItems}
                  onChange={e => updateFilter('parsedItems', e.target.value)}
                />
              </div>
              <div className="filter-group">
                <label className="filter-label">Tracking Number</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Tracking (comma-separated)..."
                  value={searchFilters.trackingNumber}
                  onChange={e => updateFilter('trackingNumber', e.target.value)}
                />
              </div>
              <div className="filter-group">
                <label className="filter-label">Created From</label>
                <input
                  type="date"
                  className="form-input"
                  value={searchFilters.dateFrom}
                  onChange={e => updateFilter('dateFrom', e.target.value)}
                />
              </div>
              <div className="filter-group">
                <label className="filter-label">Created To</label>
                <input
                  type="date"
                  className="form-input"
                  value={searchFilters.dateTo}
                  onChange={e => updateFilter('dateTo', e.target.value)}
                />
              </div>
              <div className="filter-group">
                <label className="filter-label">Collection From</label>
                <input
                  type="date"
                  className="form-input"
                  value={searchFilters.collectionDateFrom}
                  onChange={e => updateFilter('collectionDateFrom', e.target.value)}
                  disabled={searchFilters.noCollectionDate}
                />
              </div>
              <div className="filter-group">
                <label className="filter-label">Collection To</label>
                <input
                  type="date"
                  className="form-input"
                  value={searchFilters.collectionDateTo}
                  onChange={e => updateFilter('collectionDateTo', e.target.value)}
                  disabled={searchFilters.noCollectionDate}
                />
              </div>
              <div className="filter-group" style={{ display: 'flex', alignItems: 'center', paddingTop: 24 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={searchFilters.noCollectionDate}
                    onChange={e => updateFilter('noCollectionDate', e.target.checked)}
                  />
                  No collection date
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="filter-actions" style={{ justifyContent: 'flex-start', gap: 'var(--space-3)' }}>
          <button type="button" className="btn btn-secondary" onClick={clearFilters}>
            Clear
          </button>
          <button type="button" className="btn btn-primary" onClick={searchDeals} disabled={loading}>
            {loading ? 'Searching...' : 'Search'}
          </button>
          {hasSearched && deals.length > 0 && (
            <span className="text-sm text-muted" style={{ marginLeft: 'auto' }}>
              {deals.length} deals found
            </span>
          )}
        </div>
      </div>

      {/* Results */}
      <div className="card">
        {/*
          A short list that looks complete is the failure this exists to stop:
          the operator reads the row count as the whole picture. Sits ABOVE the
          loading ternary so it is not buried inside one result state, and is a
          bar rather than a toast — there is no global toast container, so a
          toast would silently no-op on any page that does not mount its own.
        */}
        {incomplete && (
          <div
            role="alert"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 'var(--space-3)',
              padding: 'var(--space-3)',
              borderBottom: '1px solid var(--color-border)',
              background: 'var(--color-warning-bg, #fff8e1)',
            }}
          >
            {incomplete.reason === 'truncated' ? (
              <span>
                Showing the first <strong>{incomplete.count}</strong> matches —
                refine your search to narrow it.
              </span>
            ) : (
              <span>
                <strong>{incomplete.count}</strong>{' '}
                {incomplete.count === 1 ? 'row' : 'rows'} could not be loaded
                {incomplete.reason === 'rate_limited' && ' (rate limited)'} — this
                list is incomplete.
              </span>
            )}
            <button
              className="btn btn-xs btn-secondary"
              onClick={() => searchDeals({ bypassCache: true })}
            >Retry</button>
          </div>
        )}
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-12)' }}>
            <div className="loading-spinner" />
          </div>
        ) : !hasSearched ? (
          <div className="empty-state">
            <div className="empty-state-title">Search Deals</div>
            <div className="empty-state-description">
              Enter search criteria and click Search to find deals.
            </div>
          </div>
        ) : searchError ? (
          <div className="empty-state" role="alert">
            <div className="empty-state-title">Search Failed</div>
            <div className="empty-state-description">
              {searchError} — try again, and report it if it keeps happening.
            </div>
          </div>
        ) : deals.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-title">No Deals Found</div>
            <div className="empty-state-description">
              Try adjusting your search filters.
            </div>
          </div>
        ) : (
          <>
            {/* Results header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: 'var(--space-3)',
              borderBottom: '1px solid var(--color-border)',
              background: 'var(--color-bg-secondary)'
            }}>
              <span className="text-muted">{deals.length} deals</span>
              <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                <button className="btn btn-xs btn-secondary" onClick={exportCSV}>Export CSV</button>
                <button className="btn btn-xs btn-secondary" onClick={refreshCache}>Refresh</button>
              </div>
            </div>

            {/* Results list */}
            <div style={{ padding: 'var(--space-3)' }}>
              {deals.map(deal => {
                const items = parseItems(deal.parsed_items)
                const firstItem = items.length > 0 ? parseItemLine(items[0]) : null
                const moreCount = items.length - 1
                const isExpanded = expandedDeals.has(deal.id)

                const toggleExpand = (e) => {
                  e.stopPropagation()
                  setExpandedDeals(prev => {
                    const next = new Set(prev)
                    if (next.has(deal.id)) {
                      next.delete(deal.id)
                    } else {
                      next.add(deal.id)
                    }
                    return next
                  })
                }

                return (
                  <div
                    key={deal.id}
                    style={{
                      borderBottom: '1px solid var(--color-border)'
                    }}
                  >
                    <div
                      onClick={() => openDealModal(deal)}
                      style={{
                        padding: 'var(--space-3)',
                        cursor: 'pointer',
                        display: 'flex',
                        gap: 'var(--space-3)',
                        alignItems: 'flex-start'
                      }}
                      className="hover-row"
                    >
                      {/* Main content */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {/* Item name and +more */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 4 }}>
                          <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>
                            {firstItem?.name || deal.dealname}
                          </span>
                          {moreCount > 0 && (
                            <button
                              onClick={toggleExpand}
                              style={{
                                fontSize: '0.75rem',
                                color: 'var(--color-primary)',
                                background: 'var(--color-bg-secondary)',
                                padding: '2px 8px',
                                borderRadius: 4,
                                border: 'none',
                                cursor: 'pointer'
                              }}
                            >
                              {isExpanded ? '− hide' : `+${moreCount} more`}
                            </button>
                          )}
                        </div>

                        {/* Item details */}
                        {firstItem?.details && (
                          <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', marginBottom: 4 }}>
                            {firstItem.details}
                          </div>
                        )}

                        {/* Qty, SP, BP */}
                        <div style={{ display: 'flex', gap: 'var(--space-3)', fontSize: '0.8rem', marginBottom: 4 }}>
                          {firstItem && (
                            <>
                              <span>×{firstItem.qty || 1}</span>
                              {firstItem.sp != null && <span style={{ color: 'var(--color-success)' }}>SP £{firstItem.sp}</span>}
                              {firstItem.bp != null && <span style={{ color: 'var(--color-info)' }}>BP £{firstItem.bp}</span>}
                            </>
                          )}
                        </div>

                        {/* Email and Deal ID */}
                        <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontFamily: 'monospace', fontSize: '0.75rem', opacity: 0.7 }}><DealLink deal={deal} onOpen={() => openDealModal(deal)} /></span>
                          <span>{deal.seller_email || deal.contact_email || '-'}</span>
                        </div>
                      </div>

                      {/* Right side - Stage and date */}
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ marginBottom: 4 }}>
                          <span className="badge">{dealStageLabel(deal)}</span>
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                          {formatDate(deal.createdate)}
                        </div>
                      </div>
                    </div>

                    {/* Expanded items */}
                    {isExpanded && moreCount > 0 && (
                      <div style={{
                        padding: '0 var(--space-3) var(--space-3) var(--space-3)',
                        background: 'var(--color-bg-secondary)',
                        borderTop: '1px dashed var(--color-border)'
                      }}>
                        {items.slice(1).map((itemLine, idx) => {
                          const item = parseItemLine(itemLine)
                          return (
                            <div
                              key={idx}
                              style={{
                                padding: 'var(--space-2) 0',
                                borderBottom: idx < items.length - 2 ? '1px solid var(--color-border)' : 'none'
                              }}
                            >
                              <div style={{ fontWeight: 500, fontSize: '0.85rem' }}>
                                {item?.name || itemLine}
                              </div>
                              {item?.details && (
                                <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                                  {item.details}
                                </div>
                              )}
                              <div style={{ display: 'flex', gap: 'var(--space-2)', fontSize: '0.75rem', marginTop: 2 }}>
                                <span>×{item?.qty || 1}</span>
                                {item?.sp != null && <span style={{ color: 'var(--color-success)' }}>SP £{item.sp}</span>}
                                {item?.bp != null && <span style={{ color: 'var(--color-info)' }}>BP £{item.bp}</span>}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </>
        )}
      </div>

      {/* Deal Detail Modal */}
      {selectedDeal && (
        <DealDetailModal
          deal={selectedDeal}
          onClose={closeDealModal}
          onDealUpdated={refreshDeals}
        />
      )}
    </div>
  )
}

export default AllDeals
